import { useQuery } from '@tanstack/react-query';
import { listTrustAccountV2 } from 'services/billingServices';

export const CLIENT_TRUST_LEDGER_QUERY_KEY = 'client-trust-ledger';

/**
 * Normalizes list-trust-account-v2 response for the client trust ledger table.
 *
 * @param {object} raw
 * @returns {object}
 */
export const hasTrustLedgerTransactions = (ledgerData) => {
  if (!ledgerData || typeof ledgerData !== 'object') return false;

  const totalCount = ledgerData.total_count ?? 0;
  const transactions = ledgerData.transactions ?? [];

  return totalCount > 0 || (Array.isArray(transactions) && transactions.length > 0);
};

/**
 * Extracts the transaction list from list-trust-account-v2 payloads.
 * Client portal responses use a top-level `data` array; legacy payloads may use `transaction_detail`.
 *
 * @param {object} source
 * @returns {Array}
 */
export const extractTrustLedgerTransactions = (source = {}) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== 'object') return [];
  if (Array.isArray(source.data)) return source.data;
  if (Array.isArray(source.transaction_detail)) return source.transaction_detail;
  if (Array.isArray(source.transactions)) return source.transactions;
  return [];
};

export const normalizeTrustLedgerResponse = (raw = {}) => {
  const nested = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : null;
  const source = nested ?? raw;
  const rawTransactions = extractTrustLedgerTransactions(source);

  return {
    transactions: rawTransactions,
    total_count: source.total_count ?? 0,
    page: source.page ?? 1,
    limit: source.limit ?? source.page_size ?? 10,
    page_count: source.page_count ?? source.total_pages ?? 0,
    trust_balance: source.trust_balance ?? source.running_trust_balance ?? source.current_trust_balance,
    temporary_funds: source.temporary_funds ?? source.designated_as_temp_trust_funds ?? source.temporary_funds_balance,
  };
};

/**
 * React Query hook for client portal trust account ledger (retainer).
 *
 * @param {object} params
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export const useTrustLedgerQuery = ({ case_id, page = 1, limit = 10, enabled = true }) => {
  return useQuery({
    queryKey: [CLIENT_TRUST_LEDGER_QUERY_KEY, case_id, page, limit],
    queryFn: async () => {
      const response = await listTrustAccountV2({
        case_id,
        account_type: 'trust',
        page,
        limit,
      });
      return normalizeTrustLedgerResponse(response?.data ?? {});
    },
    enabled: Boolean(enabled && case_id),
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: (failureCount) => failureCount < 2,
  });
};
